/**
 * revisionProgramAdapter — maps a StudyProgram (+ items) → flat `UIRevisionProgram[]`.
 *
 * API-aware (knows `study_program_items`, `day`, `start_date`, …). Day grouping is owned by the
 * view-model. Not imported by the DS.
 */
import moment from "moment"

import { type UIRevisionProgram } from "../../../ui/types"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/** Loose chapter shape nested under a study-program item. */
export type ApiStudyProgramItemChapter = {
    id?: string | number
    name?: string
    school_subject_id?: string | number
}

/** Loose study-program item (one chapter on one day). */
export type ApiStudyProgramItem = {
    id?: string | number
    day?: number
    /** Duration in hours (legacy UI appends "h"). */
    duration?: number | null
    chapter?: ApiStudyProgramItemChapter | null
}

/** Loose StudyProgram payload from `current_study_programs` / `studyPrograms` list cache. */
export type ApiStudyProgram = {
    id?: string | number
    title?: string
    class?: string
    start_date?: string | null
    /** Singular subject on the home card (legacy ParentSubscribeScreen). */
    school_subject?: ApiSchoolSubject | null
    school_subjects?: ApiSchoolSubject[] | null
    study_program_items?: ApiStudyProgramItem[] | null
}

/**
 * One program card for the parent home list (not flattened day items).
 * Mirrors legacy: title + school_subject (+ class on the type for later).
 */
export function toUIRevisionProgramSummary(program: ApiStudyProgram | null | undefined): UIRevisionProgram | null {
    if (program?.id == null || !program.title) return null
    const subjectApi = program.school_subject ?? program.school_subjects?.[0]
    return {
        id: program.id,
        title: program.title,
        className: program.class ?? "",
        subject: toUISubject(subjectApi),
    }
}

/** The bought stage programme, as the home reads it (`stage_program` of the pricing payload). */
export type ApiStageProgramRef = ApiStudyProgram & { adaptable?: boolean }

/**
 * The stage programme's row opens the page the family adapts, and reads « Adaptable jusqu'à la
 * veille » while it can. It shows from the attribution: `current_study_programs` lists a programme
 * from 7 days before its start only, so the stage programme is added when the list does not hold it.
 */
export function toUIRevisionProgramSummaries(
    programs: ApiStudyProgram[] | null | undefined,
    stageProgram?: ApiStageProgramRef | null
): UIRevisionProgram[] {
    const listed = (programs ?? []).some((p) => sameStudyProgramId(p?.id, stageProgram?.id))
    return [...(programs ?? []), ...(stageProgram && !listed ? [stageProgram] : [])]
        .map((p) => toUIRevisionProgramSummary(p))
        .filter((p): p is UIRevisionProgram => p != null)
        .map((p) =>
            sameStudyProgramId(p.id, stageProgram?.id)
                ? {
                      ...p,
                      isStageProgram: true,
                      badge: stageProgram?.adaptable ? "Adaptable jusqu'à la veille" : undefined,
                  }
                : p
        )
}

/** Route params are often strings; Redux ids are often numbers — coerce before compare. */
export function sameStudyProgramId(
    a: string | number | null | undefined,
    b: string | number | null | undefined
): boolean {
    if (a == null || b == null) return false
    return String(a) === String(b)
}

/** Absolute calendar date for program day N — same formula as legacy `DaySectionComponent.getExactDate`. */
export function studyProgramItemDate(
    startDate: string | null | undefined,
    day: number | undefined
): string | undefined {
    if (!startDate || day == null) return undefined
    return moment(startDate)
        .add(day - 1, "days")
        .toISOString()
}

/**
 * Flatten study-program items into presentational cards (one per chapter/day).
 * Items without a chapter name are dropped. Absolute `date` is derived from `start_date` + `day`.
 */
export function toUIRevisionPrograms(program: ApiStudyProgram | null | undefined): UIRevisionProgram[] {
    if (!program) return []

    const subjectsById = indexSubjectsById(program.school_subjects)
    const className = program.class ?? ""

    const out: UIRevisionProgram[] = []
    for (const item of program.study_program_items ?? []) {
        const chapter = item.chapter
        if (!chapter?.name || item.id == null) continue

        const subject = subjectsById[String(chapter.school_subject_id)]
        out.push({
            id: item.id,
            title: chapter.name,
            className,
            subject: toUISubject(subject),
            date: studyProgramItemDate(program.start_date, item.day),
            day: item.day,
            durationHours: item.duration ?? undefined,
        })
    }
    return out
}
